import { useState, useEffect, useCallback } from "react"
import { navigate } from "gatsby"
import { useQuery, useLazyQuery, useMutation, useApolloClient } from "@apollo/client"
import { Buffer } from "buffer"
import { useAnalytics } from "./useAnalytics"
import { useApp } from "./useApp"
import { useCore } from "./useCore"
import { useCheckoutContext } from "./useCheckout"
import { useShopContext } from "./useShop"
import { useRoutes } from "./useRoutes"
import { useTemplate } from "./useTemplate"

export const useShopify = () => {
  const client = useApolloClient()
  const { shop } = useShopContext()
  const { checkout } = useCheckoutContext()
  const {
    config: {
      settings: { routes },
    },
  } = useApp()
  const {
    graphql: {
      queries: { GET_COLLECTION_PRODUCT_COMPLETE, GET_PRODUCTS_BY_HANDLE, GET_CART_RECOMMENDATIONS },
    },
  } = useCore()
  const {
    product: { settingLowStockThreshold },
  } = useTemplate()

  const edgeNormaliser = object => object?.edges?.map(({ node }) => node) || object || []

  const productNormaliser = useCallback(
    product => {
      return {
        ...product,
        images: edgeNormaliser(product?.images)?.map(image => imageNormaliser(image)),
        collections:
          edgeNormaliser(product?.collections)?.map(collection => ({
            ...collection,
            image: imageNormaliser(collection?.image),
          })) || [],
        metafields: edgeNormaliser(product?.metafields),
        variants:
          edgeNormaliser(product?.variants)?.map(variant => ({
            ...variant,
            image: imageNormaliser(variant?.image),
            metafields: edgeNormaliser(variant?.metafields),
            presentmentPrices: variant?.presentmentPrices ? edgeNormaliser(variant?.presentmentPrices) : variant?.presentment_prices,
            priceV2:
              !checkout?.currencyCode && !shop?.paymentSettings?.currencyCode
                ? variant?.priceV2
                : variant?.presentmentPrices
                ? priceNormaliser(variant?.presentmentPrices, "price")
                : priceNormaliserV2(variant?.presentment_prices, "price"),
            compareAtPriceV2:
              !checkout?.currencyCode && !shop?.paymentSettings?.currencyCode
                ? variant?.compareAtPriceV2
                : variant?.presentmentPrices
                ? priceNormaliser(variant?.presentmentPrices, "compareAtPrice")
                : priceNormaliserV2(variant?.presentment_prices, "compareAtPrice"),
          })) || [],
      }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [checkout, shop]
  )

  const collectionNormaliser = collection => ({
    ...collection,
    image: imageNormaliser(collection?.image),
    metafields: edgeNormaliser(collection?.metafields),
    products: edgeNormaliser(collection?.products).map(product => productNormaliser(product)),
  })

  const imageNormaliser = (image, size = 1000) => ({
    alt: image?.altText || image?.alt || image?.asset?.alt || ``,
    src: imageUrl(image?.originalSrc || image?.src || image?.asset?.url || ``, size),
    srcSet: imageSrcSets(image?.originalSrc || image?.src || image?.asset?.url, size),
  })

  const priceNormaliser = (presentmentPrices, field) => {
    return Object.assign(
      {},
      ...edgeNormaliser(presentmentPrices)
        ?.filter(item => item[field]?.currencyCode === (checkout?.currencyCode || shop?.paymentSettings?.currencyCode))
        .map(item => ({
          ...item[field],
          local: formatMoney(item[field]?.amount, checkout?.currencyCode || shop?.paymentSettings?.currencyCode),
        }))
    )
  }

  const priceNormaliserV2 = (presentmentPrices, field) => {
    return Object.assign(
      {},
      presentmentPrices
        ?.filter(item => item[field]?.currency_code === (checkout?.currencyCode || shop?.paymentSettings?.currencyCode))
        .map(item => ({
          ...item[field],
          local: formatMoney(item[field]?.amount, checkout?.currencyCode || shop?.paymentSettings?.currencyCode),
        }))[0]
    )
  }

  const checkoutNormaliser = checkout => ({
    ...checkout,
    lineItems:
      checkout?.lineItems?.edges.map(({ node }) => ({
        ...node,
        variant: {
          ...node?.variant,
          priceV2: Object.assign(
            {},
            ...node?.variant?.presentmentPrices?.edges
              ?.filter(({ node }) => node?.price?.currencyCode === checkout?.currencyCode)
              .map(({ node }) => node?.price)
          ),
          compareAtPriceV2: Object.assign(
            {},
            ...node?.variant?.presentmentPrices?.edges
              ?.filter(({ node }) => node?.compareAtPriceV2?.currencyCode === checkout?.currencyCode)
              .map(({ node }) => node?.compareAtPriceV2)
          ),
        },
      })) || [],
  })

  const formatMoney = (amount, currency = "AUD") =>
    new Intl.NumberFormat(`en-${shop?.paymentSettings?.countryCode || "AU"}`, {
      style: "currency",
      currency: currency,
    }).format(amount)

  const imageUrl = (src, size = `master`) => {
    const dimensions = `${size}x${size}`
    let sized = src?.match(/(_\d+x\d+)|(_\d+x)/)
    const match = src?.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i)
    let formatted = src
    if (sized && sized.length) {
      formatted = src.replace(sized[0], "")
      sized = false
    }

    return match && formatted?.includes(`shopify.com`) && size !== `master`
      ? `${formatted?.split(match[0])[0]}${!sized ? `_${dimensions}` : ``}${match[0]}`.replace(/http(s)?:/, ``)
      : formatted
  }

  const imageSrcSets = (src, size) =>
    src?.includes(`shopify.com`) &&
    [1, 250, 500, 1000, 1500, 2000]
      .filter(set => !size || (size && size >= set))
      .map(set => `${imageUrl(src, String(set))} ${set}w`)
      .join(`,`)

  const onSale = (price, compareAtPrice) => compareAtPrice && price && parseInt(compareAtPrice) > parseInt(price)

  const getPagination = object => {
    const lastItem = object?.edges?.slice(-1)
    return {
      hasNextPage: object?.pageInfo?.hasNextPage,
      lastCursor: lastItem && lastItem?.length ? lastItem[0].cursor : null,
    }
  }

  const getHandle = item => item?.handle || item?.shopify?.shopifyHandle

  const getCollection = async ({
    firstCollections = 0,
    firstImages = 0,
    firstMedia = 0,
    firstMetafields = 0,
    firstProducts = 0,
    firstVariants = 0,
    handle,
  }) => {
    const { data } = await client.query({
      query: GET_COLLECTION_PRODUCT_COMPLETE,
      variables: {
        firstCollections,
        firstImages,
        firstMedia,
        firstMetafields,
        firstProducts,
        firstVariants,
        handle,
      },
    })

    return collectionNormaliser(data?.collection)
  }

  const getProducts = async ({ firstCollections = 0, firstImages = 0, firstMedia = 0, firstMetafields = 0, firstVariants = 0, handles = [] }) => {
    const { data } = await client.query({
      query: GET_PRODUCTS_BY_HANDLE(handles),
      variables: {
        firstCollections,
        firstImages,
        firstMedia,
        firstMetafields,
        firstVariants,
      },
    })

    return handles?.map(handle => productNormaliser(data[`product${handle?.replace(/-/g, "")}`]))
  }

  const getCartRecommendations = async ({ ids, first = 4, firstImages = 10, firstVariants = 0 }) => {
    const { data } = await client.query({
      query: GET_CART_RECOMMENDATIONS,
      variables: {
        ids: ids,
        firstImages,
        firstVariants,
      },
    })

    return data?.recommendations?.filter((_, index) => index < first).map(productNormaliser)
  }

  const encodeBase64 = string => Buffer.from(string).toString("base64")
  const decodeBase64 = string => Buffer.from(string, "base64").toString()

  const decodeProductId = id => {
    return id ? decodeBase64(id).split(`Product/`)[1] : id
  }

  const decodeVariantId = id => {
    return id ? decodeBase64(id).split(`ProductVariant/`)[1] : id
  }

  const encodeCollectionId = id => {
    return id ? encodeBase64(`gid://shopify/Collection/${id}`) : id
  }

  const formatProduct = product => {
    const shapeIconName = {
      square: "square",
      lumbar: "lumbar",
      round: "round",
      default: "square",
    }
    const colourGroup = product?.tags?.find(tag => tag.includes(`colourGroup:`))?.replace(`colourGroup:`, "")
    const styleGroup = product?.tags?.find(tag => tag.includes(`styleGroup:`))?.replace(`styleGroup:`, "")
    const productGroup = product?.tags?.find(tag => tag.includes(`productGroup:`))?.replace(`productGroup:`, "")
    const contentGroup = product?.tags?.find(tag => tag.includes(`contentGroup:`))?.replace(`contentGroup:`, "")
    const packGroup = product?.tags?.find(tag => tag.includes(`packGroup:`))?.replace(`packGroup:`, "")
    const size = product?.tags?.find(tag => tag.includes(`size:`))?.replace(`size:`, "")
    const shape = product?.tags?.find(tag => tag.includes(`shape:`))?.replace(`shape:`, "")
    const productCategory = product?.tags?.find(tag => tag.includes(`productType:`))?.replace(`productType:`, "")
    const material = product?.tags?.find(tag => tag.includes(`material:`))?.replace(`material:`, "")
    const colour = product?.tags?.find(tag => tag.includes(`colour:`))?.replace(`colour:`, "")
    const badge = {
      colour: product?.tags?.find(tag => tag.includes(`badgeColour:`))?.replace(`badgeColour:`, ""),
      text: product?.tags?.find(tag => tag.includes(`badgeText:`))?.replace(`badgeText:`, ""),
    }
    const title = product?.title
    const defaultVariant = product?.variants[0]

    return {
      ...product,
      images: product?.images?.map(image => imageNormaliser(image)),
      collection: product?.collections?.length ? product?.collections[0] : null,
      description: product?.description,
      priceRange: product?.priceRange,
      variants: product?.variants,
      key: product?.id,
      link: `${routes?.PRODUCT}/${getHandle(product)}`,
      tags: product?.tags,
      title,
      colour,
      material,
      sizeTag: size,
      shapeTag: shape,
      productCategory: productCategory,
      shapeIcon: shape ? shapeIconName[shape.toLowerCase()] : shapeIconName.default,
      defaultVariant: defaultVariant,
      badge: badge,
      contentGroup,
      styleGroup,
      productGroup,
      colourGroup,
      packGroup,
    }
  }

  const formatBadge = (product, selectedVariant) => {
    const threshold = settingLowStockThreshold ? parseInt(settingLowStockThreshold) : null

    // show default badge (from product tag)
    if (product?.badge?.colour && product?.badge?.text) {
      return product.badge
    }

    // show out of stock
    if (selectedVariant && !selectedVariant?.availableForSale && !selectedVariant?.available) {
      return {
        colour: "pink",
        text: "Sold Out!",
      }
    }

    // show "on sale"
    if (
      (selectedVariant && selectedVariant?.compareAtPriceV2 && Object.keys(selectedVariant?.compareAtPriceV2).length > 0) ||
      (selectedVariant?.compare_at_price && selectedVariant?.price && selectedVariant?.compare_at_price > selectedVariant?.price)
    ) {
      return {
        colour: "yellow",
        text: "Sale!",
      }
    }
    // show low stock
    if (threshold && selectedVariant && selectedVariant?.quantityAvailable <= 0 && selectedVariant?.availableForSale) {
      return {
        colour: "blue",
        text: "Delayed",
      }
    }
    // show low stock
    if (
      threshold &&
      selectedVariant &&
      (selectedVariant?.inventory_quantity <= threshold ||
        (selectedVariant?.quantityAvailable > 0 && selectedVariant?.quantityAvailable <= threshold))
    ) {
      return {
        colour: "red",
        text: "Last Few!",
      }
    }

    return null
  }

  return {
    decodeProductId,
    decodeVariantId,
    encodeCollectionId,
    client,
    useQuery,
    useLazyQuery,
    useMutation,
    onSale,
    imageUrl,
    imageSrcSets,
    formatMoney,
    edgeNormaliser,
    imageNormaliser,
    checkoutNormaliser,
    priceNormaliser,
    productNormaliser,
    collectionNormaliser,
    getHandle,
    getCollection,
    getProducts,
    getCartRecommendations,
    formatProduct,
    formatBadge,
    getPagination,
  }
}

export const useShopifyProduct = () => {
  const { activeProduct, setActiveProduct } = useApp()

  const selectProduct = useCallback(
    (product, path) => {
      if (path?.includes("products")) {
        if (!activeProduct) setActiveProduct(product)
      } else {
        if (activeProduct !== false) setActiveProduct(false)
      }
    },
    [activeProduct, setActiveProduct]
  )

  return { activeProduct, selectProduct }
}

export const useShopifyVariants = ({ firstAvailable = true, useParameter = false, loading = false, product }) => {
  const { trackProductView } = useAnalytics()
  const {
    activeVariant,
    activeProduct,
    checkout,
    setActiveVariant,
    shop,
    config: {
      settings: { params },
    },
  } = useApp()
  const {
    helpers: { encodeShopifyId, decodeShopifyId },
  } = useCore()
  const { getUrlParameter, setUrlParameter } = useRoutes()
  const { id, variants } = product || {}

  const currentVariant = getUrlParameter(params?.variant)
  const defaultVariant =
    (useParameter && variants?.find(({ id }) => id === encodeShopifyId(currentVariant, "ProductVariant"))) ||
    (firstAvailable && variants?.find(({ availableForSale }) => availableForSale)) ||
    variants[0]

  const [selectedOptions, setOptions] = useState(defaultVariant?.selectedOptions || [])

  const handleVariant = useCallback(
    option => {
      setOptions(selectedOptions.map(selectedOption => (selectedOption.name === option.name ? option : selectedOption)))
    },
    [selectedOptions, setOptions]
  )

  useEffect(() => {
    if ((checkout?.currencyCode || shop?.paymentSettings?.currencyCode) && !loading) {
      setOptions(defaultVariant?.selectedOptions)
    }
  }, [checkout?.currencyCode, id, loading, shop?.paymentSettings?.currencyCode, variants?.length, defaultVariant?.selectedOptions])

  useEffect(() => {
    setActiveVariant(
      variants.find(
        ({ selectedOptions: variantOptions }) =>
          variantOptions?.filter(
            variantOption => variantOption.value === selectedOptions.find(selectedOption => selectedOption.name === variantOption.name)?.value
          )?.length === selectedOptions?.length
      ) || null
    )
  }, [selectedOptions, setActiveVariant, variants])

  useEffect(() => {
    if (useParameter && activeVariant && !loading) {
      if (currentVariant !== encodeShopifyId(activeVariant.id, "ProductVariant") && activeVariant.id !== defaultVariant.id) {
        navigate(setUrlParameter(params?.variant, decodeShopifyId(activeVariant.id, "ProductVariant")), { replace: true })
      }
      trackProductView(activeProduct)
    }
  }, [
    activeVariant,
    activeProduct,
    currentVariant,
    decodeShopifyId,
    defaultVariant.id,
    encodeShopifyId,
    loading,
    params?.variant,
    setUrlParameter,
    trackProductView,
    useParameter,
  ])

  return { activeVariant, handleVariant, selectedOptions }
}
